import { Descendant } from "slate";

import { IFilterUpdate } from "components/filters/interfaces";
import { IFilters } from "constants/filters";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction } from "constants/tickets/batch-action";
import { IDraft, ITicket, TicketPriority, TicketSilo, TicketStatus } from "interfaces/entities/ticket";
import { TicketSortingField, TicketSortingOrder } from "interfaces/sorting";
import { Action } from "services/websocket/interfaces";
import { IEntityFetchFailure } from "store/entities/actions";
import { IAgent } from "store/entities/agents";
import { ITicketRequester } from "store/entities/tickets/interfaces";
import { IAction } from "store/helper";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { TicketsViewActionsNames } from "./actions";

export interface ITicketsViewParams extends IFilters {
  query?: string;
  page?: number;
  route?: TicketsRoutes;
  silo?: TicketSilo;
  sortBy?: TicketSortingField;
  order?: TicketSortingOrder;
}

export type TicketMessage = React.ReactElement | string;

export interface ITicketsViewSetRoutePayload {
  route: TicketsRoutes;
}

export interface ITicketsViewSetTicketsDataPayload {
  data: ITicket[];
  totalPages: number;
  totalResults: number;
}

export interface ITicketsViewSetQueryParams {
  params: ITicketsViewParams;
}

export interface ITicketsViewSelectCustomViewPayload {
  id: string;
}

export interface ITicketsViewSelectStandardViewPayload {
  route: TicketsRoutes;
}

export type ITicketsViewUpdateFilterPayload = IFilterUpdate;

export interface ITicketsViewSetViewPayload {
  route: TicketsRoutes;
  filters: Partial<IFilters>;
  activeCustomViewId?: string;
  query?: string;
  page?: number;
  silo?: TicketSilo;
  sortBy?: TicketSortingField;
  order?: TicketSortingOrder;
}

export interface ITicketsViewBatchUpdatePayloadData {
  assignee?: {
    id: string;
    name: string;
  };
  team?: {
    id: string;
    name: string;
  };
}

export interface ITicketsViewBatchUpdatePayload {
  ticketIds: string[];
  batchAction: TicketsBatchAction;
  data?: ITicketsViewBatchUpdatePayloadData;
}

export type ITicketsViewBatchUpdateSuccessPayload = ITicketsViewBatchUpdatePayload;

export interface ITicketsViewCreateTicketPayload {
  subject: string;
  requester: ITicketRequester;
  assignedTeamId: string;
  message: string | Descendant[];
  priority: TicketPriority;
  attachments?: File[];
  assigneeId?: string;
  richTextPresent?: boolean;
  isPrivate?: boolean;
  tags?: string[];
  status: TicketStatus;
  cannedResponseIds?: string[];
  transactionID?: string;
  peopleInTheLoop?: string[];
}

export interface ITicketsViewDeleteTicketPayload {
  id: string;
}

export interface ITicketsViewUpdateTagsPayload {
  ticketId: string;
  tagIds: string[];
}

export interface ITicketsViewUpdatePriorityPayload {
  ticketId: string;
  priority: TicketPriority;
  interaction: string;
}

export interface ITicketsViewUpdateStatusPayload {
  ticketId: string;
  status: TicketStatus;
  interaction: string;
  usingShortcut: boolean;
}

export interface ITicketsViewUpdateSubjectPayload {
  ticketId: string;
  subject: string;
}

export interface ITicketsViewUpdateRequesterPayload {
  ticketId: string;
  requesterName: string;
  requesterEmail: string;
}

export interface ITicketsViewAddFollowerPayload {
  ticketID: string;
  agentID: string;
}

export interface ITicketsViewDeleteFollowerPayload {
  ticketID: string;
  agentID: string;
}

export type ITicketsViewUpdateRequesterSuccessPayload = ITicketsViewUpdateRequesterPayload;

export interface ITicketsViewUpdateCCsPayload {
  ticketId: string;
  ccs: string[];
}

export type ITicketsViewUpdateCCsSuccessPayload = ITicketsViewUpdateCCsPayload;
export interface ITicketsViewUpdateAssigneePayload {
  ticketId: string;
  assigneeId: string;
}

export interface ITicketsViewUpdateAssignedTeamPayload {
  ticketId: string;
  teamId: string;
}

export interface ITicketsViewSendMessagePayload {
  ticketId: string;
  message: string | Descendant[];
  richTextPresent?: boolean;
  isPrivate: boolean;
  attachments?: File[];
  transactionID?: string;
  status?: TicketStatus;
  mentionedAgentIDs?: string[];
  handleClearMessageBox?: () => void;
  usingShortcut?: boolean;
  cannedResponseIds?: string[];
  replySuggestionIds?: string[];
}

export interface ITicketsViewSendMessageSuccessPayload {
  ticket: ITicket;
  originalRequest: ITicketsViewSendMessagePayload;
  agent: IAgent;
}

export interface ITicketsViewSaveDraftPayload extends IDraft {
  ticketId: string;
}

export interface ITicketsViewClearDraftPayload {
  ticketId: string;
}

export interface ITicketsViewUnspamPayload {
  ticketId: string;
}

export interface ITicketsViewFromAllTeamsPayload {
  route: TicketsRoutes;
}

export interface ITicketsViewNavigationItemCount {
  route: TicketsRoutes;
  count: number;
}

export interface ITicketsViewFetchOriginalMessage {
  eventId: string;
}

export interface ITicketsViewFetchOriginalMessageSuccess {
  ticketId: string;
  eventId: string;
  message: {
    html: string;
    text: string;
  };
}

export interface ITicketsViewFetchOriginalMessageFailure extends IEntityFetchFailure {
  ticketId: string;
}
export interface ITicketsViewFetchUnstrippedMessage {
  eventId: string;
}

export interface ITicketsViewFetchUnstrippedMessageSuccess {
  ticketId: string;
  eventId: string;
  message: {
    html: string;
    text: string;
  };
}

export interface ITicketsViewFetchUnstrippedMessageFailure extends IEntityFetchFailure {
  ticketId: string;
}

export interface IPerformBulkActionPayload {
  selectedIds: string[];
  pendingActions: {
    status: TicketStatus | null;
    priority: TicketPriority | null;
    tagIds: string[];
    teamId: string | null;
    agentId: string | null;
  };
}

export interface IMergeTicketsPayload {
  parentTicketId: string;
  childTicketIds: string[];
  onFinish: () => void;
}
export interface IMergeTicketsDonePayload {
  successCount: number;
  failureCount: number;
  onFinish: () => void;
}

export enum TicketAgentActivityType {
  TypingStart = "ticketTypingStart",
  TypingStop = "ticketTypingStop",
  ViewingStart = "ticketViewingStart",
  ViewingStop = "ticketViewingStop",
}

export interface IOnTicketUpdatedPayload {
  ticketId: string;
  action?: Action;
  ticket?: ITicket;
}

export interface IPerformBulkSiloChangePayload {
  selectedIds: string[];
  newSilo: TicketSilo;
}

export interface INotifyTicketAgentActivityPayload {
  agentActivity: TicketAgentActivityType;
  ticketID: string;
}

export interface IPerformBulkActionDonePayload {
  successCount: number;
  failureCount: number;
}

export interface IPerformBulkSiloChangeDonePayload extends IPerformBulkActionDonePayload {
  silo: TicketSilo;
}

export type IFetchTicketsAction = IAction<
  | TicketsViewActionsNames.SET_SILO
  | TicketsViewActionsNames.UPDATE_FILTER
  | TicketsViewActionsNames.PERFORM_BULK_SILO_CHANGE_DONE
  | TicketsViewActionsNames.PERFORM_BULK_DELETE_DONE
  | TicketsViewActionsNames.SET_VIEW
  | TicketsViewActionsNames.CLEAR_SEARCH_QUERY
  | TicketsViewActionsNames.SET_INITIAL_ROUTE
  | TicketsViewActionsNames.UPDATE_BATCH_SUCCESS
  | TicketsViewActionsNames.SET_PAGE
  | TicketsViewActionsNames.REFRESH_TICKETS
  | TicketsViewActionsNames.SET_SILO
  | TicketsViewActionsNames.UPDATE_CURRENT_SORTING
  | TicketsViewActionsNames.RESET_CURRENT_SORTING
>;
